import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { checkAuth } from "../redux/authSlice";
import api from "../services/api";
import { CheckCircle, XCircle, Loader, AlertCircle, ArrowRight, ShieldCheck, CreditCard, RefreshCw, Smartphone, Zap, Shield, Cpu, ChevronRight, Clock } from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { cn } from "../utils/cn";

export default function PaymentStatusPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [status, setStatus] = useState("verifying"); // verifying | success | failed
    const [message, setMessage] = useState("");
    const [pollCount, setPollCount] = useState(0);

    useEffect(() => {
        // ✅ SECURE: Poll backend for subscription status
        // Razorpay webhook handles verification on server
        // Client just checks if subscription became active
        const pollInterval = setInterval(async () => {
            setPollCount(prev => prev + 1);

            try {
                const { data } = await api.get("/subscriptions/current");
                const subscription = data.subscription;

                if (subscription && subscription.isActive) {
                    setStatus("success");
                    setMessage("Your subscription has been activated successfully! Redirecting…");
                    dispatch(checkAuth());

                    setTimeout(() => navigate("/dashboard/crm"), 2000);
                    clearInterval(pollInterval);
                } else if (pollCount > 20) {
                    // After 20 polls (40 seconds), stop checking
                    setStatus("pending");
                    setMessage("Payment is being processed. Your subscription will activate shortly. You can close this page.");
                    clearInterval(pollInterval);
                }
            } catch (err) {
                // Still checking, don't show error yet
                console.log("Checking subscription status…");
            }
        }, 2000); // Poll every 2 seconds

        return () => clearInterval(pollInterval);
    }, [dispatch, navigate]);

    return (
        <div className="min-h-screen bg-[#F7F9FB] flex items-center justify-center p-6 font-sans">
            <Card className="w-full max-w-md bg-white border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden rounded-[8px] p-0">
                <div className="p-10 flex flex-col items-center text-center space-y-8">
                    
                    {status === "verifying" && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-[#EEF4FF] flex items-center justify-center relative">
                                <Loader size={32} className="text-[#0176D3] animate-spin" strokeWidth={2.5} />
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-[20px] font-bold text-[#080707]">Verifying Transaction</h2>
                                <p className="text-[13px] text-[#706E6B] leading-relaxed px-6">
                                    We are confirming your payment details with the gateway. This will only take a moment.
                                </p>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-[#F3F2F2] rounded-full">
                                <RefreshCw size={12} className="text-[#706E6B] animate-spin" />
                                <span className="text-[10px] font-bold text-[#706E6B] uppercase ">Processing…</span>
                            </div>
                        </>
                    )}

                    {status === "success" && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-[#EBF7E6] flex items-center justify-center">
                                <CheckCircle size={40} className="text-[#2E844A]" strokeWidth={2} />
                            </div>
                            <div className="space-y-3">
                                <h2 className="text-[22px] font-bold text-[#080707]">Subscription Activated</h2>
                                <p className="text-[14px] text-[#5C5C5C] leading-relaxed px-4">{message}</p>
                            </div>
                            <div className="pt-6 w-full space-y-4">
                                <div className="flex items-center justify-center gap-3 text-[12px] font-medium text-[#0176D3]">
                                    <div className="w-4 h-4 rounded-full border-2 border-[#0176D3] border-t-transparent animate-spin" />
                                    Setting up your account
                                </div>
                                <Button 
                                    variant="brand" 
                                    className="w-full h-12 text-[14px] font-bold rounded-[4px] shadow-sm shadow-blue-500/20" 
                                    icon={ChevronRight} 
                                    onClick={() => navigate("/dashboard/crm")}
                                >
                                    Proceed to Dashboard
                                </Button>
                            </div>
                        </>
                    )}

                    {status === "pending" && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-[#FEF1E5] flex items-center justify-center">
                                <Clock size={40} className="text-[#DD7A01]" strokeWidth={2} />
                            </div>
                            <div className="space-y-3">
                                <h2 className="text-[22px] font-bold text-[#080707]">Processing Payment</h2>
                                <p className="text-[14px] text-[#5C5C5C] leading-relaxed px-4">{message}</p>
                            </div>
                            <div className="flex flex-col w-full gap-3 pt-6">
                                <Button
                                    variant="brand"
                                    className="h-12 font-bold rounded-[4px]"
                                    onClick={() => navigate("/dashboard/subscription")}
                                >
                                    Back to Subscription
                                </Button>
                            </div>
                        </>
                    )}

                    {status === "failed" && (
                        <>
                            <div className="w-20 h-20 rounded-full bg-[#FEF1EE] flex items-center justify-center">
                                <XCircle size={40} className="text-[#BA0517]" strokeWidth={2} />
                            </div>
                            <div className="space-y-3">
                                <h2 className="text-[22px] font-bold text-[#080707]">Verification Failed</h2>
                                <p className="text-[14px] text-[#5C5C5C] leading-relaxed px-4">{message}</p>
                            </div>
                            <div className="flex flex-col w-full gap-3 pt-6">
                                <Button 
                                    variant="brand" 
                                    className="h-12 font-bold rounded-[4px]" 
                                    icon={RefreshCw} 
                                    onClick={() => navigate("/dashboard/subscription")}
                                >
                                    Try Again
                                </Button>
                                <Button 
                                    variant="secondary" 
                                    className="h-12 font-bold rounded-[4px]" 
                                    onClick={() => navigate("/dashboard/crm")}
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    )}
                </div>
                
                <div className="px-8 py-4 bg-[#F7F9FB] border-t border-[#F3F2F2] flex items-center justify-between text-[11px] text-[#706E6B] font-medium">
                    <div className="flex items-center gap-2">
                        <Shield size={14} className="text-[#0176D3]" />
                        Secure Checkout
                    </div>
                    <div>Ref ID: {Math.random().toString(36).substring(7).toUpperCase()}</div>
                </div>
            </Card>
        </div>
    );
}
